import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Collapse, Nav } from 'react-bootstrap';
import { NavLink, useLocation } from 'react-router-dom';
import NavbarVerticalMenuItem from './NavbarVerticalMenuItem';
import { useAppContext } from 'providers/AppProvider';
import Guard from 'components/access-control/rbac/Guard';
import { usePermissions } from 'components/access-control/rbac/usePermissions';

const CollapseItems = ({ route }) => {
  const { pathname } = useLocation();

  const openCollapse = childrens => {
    const checkLink = children => {
      if (children.to === pathname) {
        return true;
      }
      return (
        Object.prototype.hasOwnProperty.call(children, 'children') &&
        children.children.some(checkLink)
      );
    };
    return childrens.some(checkLink);
  };

  const [open, setOpen] = useState(openCollapse(route.children));

  return (
    <Nav.Item as="li">
      <Nav.Link
        onClick={() => {
          setOpen(!open);
        }}
        className={classNames('dropdown-indicator cursor-pointer', {
          'text-500': !route.active
        })}
        aria-expanded={open}
        // {...route}
      >
        <NavbarVerticalMenuItem route={route} />
      </Nav.Link>
      <Collapse in={open}>
        <Nav className="flex-column nav" as="ul">
          <NavbarVerticalMenu routes={route.children} />
        </Nav>
      </Collapse>
    </Nav.Item>
  );
};

CollapseItems.propTypes = {
  route: PropTypes.shape({
    name: PropTypes.string.isRequired,
    icon: PropTypes.string,
    children: PropTypes.array.isRequired,
    active: PropTypes.bool
  }).isRequired
};


export const getRoutePrivilege = (route) => {
  if (route.privilege) return { type: "single", value: route.privilege };
  if (route.privileges)
    return {
      type: "multi",
      value: route.privileges,
      all: route.all,
      any: route.any,
    };
  return null;
};


const userCanAccessRoute = (route, can, canAny) => {
  if (route.privilege) {
    return can(route.privilege);
  }

  if (route.privileges) {
    if (route.all) return route.privileges.every((p) => can(p));
    return canAny(route.privileges); 
  }

  return true;
};


const NavbarVerticalMenu = ({ routes }) => {
  const {
    config: { showBurgerMenu },
    setConfig,
  } = useAppContext();

  const { can, canAny } = usePermissions();

  const handleNavItemClick = () => {
    if (showBurgerMenu) {
      setConfig("showBurgerMenu", !showBurgerMenu);
    }
  };

  return routes.map((route) => {
    // check access before rendering anything
    const canAccess = userCanAccessRoute(route, can, canAny);
    if (!canAccess) return null;

    const privilegeConfig = getRoutePrivilege(route);

    const GuardWrapper = ({ children }) => {
      if (!privilegeConfig) return children; // no restrictions

      if (privilegeConfig.type === "single") {
        return <Guard privilege={privilegeConfig.value}>{children}</Guard>;
      }

      return (
        <Guard
          privileges={privilegeConfig.value}
          all={privilegeConfig.all}
          any={privilegeConfig.any}
        >
          {children}
        </Guard>
      );
    };

    // render leaf routes
    if (!route.children) {
      return (
        <Nav.Item as="li" key={route.name} onClick={handleNavItemClick}>
          <NavLink
            end={route.exact}
            to={route.to}
            target={route?.newtab && "_blank"}
            onClick={() =>
              route.name === "Modal"
                ? setConfig("openAuthModal", true)
                : undefined
            }
            className={({ isActive }) =>
              isActive && route.to !== "#!" ? "active nav-link" : "nav-link"
            }
          >
            <GuardWrapper>
              <NavbarVerticalMenuItem route={route} />
            </GuardWrapper>
          </NavLink>
        </Nav.Item>
      );
    }

    // render collapsible groups
    return (
      <GuardWrapper key={route.name}>
        <CollapseItems route={route} />
      </GuardWrapper>
    );
  });
};


NavbarVerticalMenu.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape(NavbarVerticalMenuItem.propTypes))
    .isRequired
};

export default NavbarVerticalMenu;
